import { KDrawer } from '@/ui-library/components/molecules/KDrawer';
import { Flex } from '@chakra-ui/react';
import { KText } from '@/ui-library/components/text';
import { useQuery, useMutation } from '@tanstack/react-query';
import { getSurveyFormData, SURVEY_API } from './api/survey.api';
import { CenterSpinner } from '@/components/ats';
import { useMemo } from 'react';
import { SurveyForm } from './SurveyForm';

interface SurveyDrawerProps {
  isOpen?: boolean;
  onClose?: () => void;
  isAdhoc?: boolean;
  activity?: any;
  application?: any;
  stageId?: string;
  refetch?: () => void;
  isScheduled?: boolean;
  setIsScheduled?: (value: boolean) => void;
}

export const SurveyDrawer: React.FC<SurveyDrawerProps> = ({
  isOpen = true,
  onClose = () => {},
  isAdhoc = false,
  activity,
  application,
  stageId,
  refetch = () => {},
  isScheduled,
  setIsScheduled,
}) => {
  // Get survey details - OLD API
  const { data: surveyDetails, isLoading: isGetSurveyLoading } = useQuery({
    queryKey: ['getSurvey', activity?.data?.id],
    queryFn: () => SURVEY_API.getSurvey(activity?.data?.id),
    enabled: !!activity?.data?.id,
    staleTime: 0,
  });

  // Get survey details - NEW API
  const { data: surveyData, isLoading: isGetLoading } = useQuery({
    queryKey: ['getNewSurvey', application?.id, stageId, activity?.id],
    queryFn: () =>
      SURVEY_API.getNewSurvey({
        ats_application_id: application?.id,
        ats_job_stage_id: stageId || '',
        ats_job_stage_action_id: activity?.id,
      }),
    enabled:
      !!application?.id && !!stageId && !!activity?.id && !activity?.data,
    staleTime: 0,
  });

  const isCreateFlow = !activity?.data?.id;

  // Create Survey Mutation
  const createSurveyMutation = useMutation({
    mutationFn: (formData: FormData) => SURVEY_API.createSurvey(formData),
    onSuccess: () => {
      onClose();
      refetch();
    },
  });

  // Update Survey Mutation
  const updateSurveyMutation = useMutation({
    mutationFn: ({
      surveyId,
      formData,
    }: {
      surveyId: string;
      formData: FormData;
    }) => SURVEY_API.updateSurvey(surveyId, formData),
    onSuccess: () => {
      onClose();
      refetch();
    },
  });

  // Transform surveyData to match the expected activity structure
  const transformedActivity = useMemo(() => {
    // For create flow, we need surveyData
    if (isCreateFlow) {
      if (!surveyData?.survey) return null;

      const { survey, to_participants = [] } = surveyData;
      return {
        id: survey.job_stage_action_id,
        label: survey.name,
        survey_type: survey.survey_type,
        form_template: survey.ats_form_template || {},
        candidate_comm_template: {
          ...(survey.ats_comm_template || {}),
          name: survey.ats_comm_template?.email_template_name || survey.ats_comm_template?.name,
        },
        ats_shared_email: survey.ats_shared_email,
        candidate_comm_sender: survey.sender || {},
        cc_participants: survey.cc || [],
        bcc_participants: survey.bcc || [],
        to_participants: to_participants,
        status: survey.status,
      };
    }

    // For edit flow, we need surveyDetails
    if (!surveyDetails?.id) return null;

    const to_participants = surveyDetails?.participants || [];
    return {
      id: surveyDetails?.id,
      label: surveyDetails?.name,
      survey_type: surveyDetails?.survey_type,
      form_template: surveyDetails?.ats_form_template || {},
      candidate_comm_template: {
        ...(surveyDetails?.ats_comm_template || {}),
        name: surveyDetails?.ats_comm_template?.email_template_name || surveyDetails?.ats_comm_template?.name,
      },
      ats_shared_email: surveyDetails?.ats_shared_email,
      candidate_comm_sender: surveyDetails?.sender || {},
      cc_participants: surveyDetails?.cc || [],
      bcc_participants: surveyDetails?.bcc || [],
      to_participants: to_participants,
      status: surveyDetails?.status,
      scheduled_at: surveyDetails?.scheduled_at,
      timezone: surveyDetails?.timezone,
    };
  }, [isCreateFlow, surveyData, surveyDetails]);

  const handleSave = async (finalParams: any) => {
    try {
      // Convert params to FormData
      const fd = new FormData();
      const formData = getSurveyFormData(fd, finalParams);

      if (!isCreateFlow) {
        const result = await updateSurveyMutation.mutateAsync({
          surveyId: surveyDetails?.id,
          formData,
        });
        if (result?.data?.id) {
          return { data: result.data };
        }
        return { errors: [] };
      } else {
        // Add required fields for create flow

        if (!isAdhoc) {
          formData.append('ats_job_stage_action_id', activity?.id);
        }
        formData.append('ats_application_id', application?.id);
        formData.append('ats_job_stage_id', stageId || '');
        formData.append('ats_job_id', application?.job_id);
        const result = await createSurveyMutation.mutateAsync(formData);
        if (result?.data?.id) {
          return { data: result.data };
        }
        return { errors: [] };
      }
    } catch (error: any) {
      return {
        errors: [
          error?.response?.data?.message || error?.message || 'Unknown error',
        ],
      };
    }
  };

  const renderContent = () => {
    const isSaving =
      createSurveyMutation.isPending || updateSurveyMutation.isPending;

    return (
      <SurveyForm
        activity={transformedActivity || {}}
        isCreateFlow={isCreateFlow}
        isLoading={isSaving}
        onSave={handleSave}
        onCancel={onClose}
        successMessage={{
          create: 'Survey created successfully',
          update: 'Survey updated successfully',
        }}
        errorMessage={{
          create: 'Survey creation failed',
          update: 'Survey updation failed',
        }}
        hasSchedule={true}
        isScheduled={isScheduled}
        setIsScheduled={setIsScheduled}
        isInterviewPlan={false}
      />
    );
  };

  const renderHeader = () => {
    if (isGetLoading) return null;
    return (
      <Flex p="20px 24px 0px 24px">
        <KText size="md-medium" color="secondary-900">
          {isCreateFlow ? 'Send survey' : 'Edit survey'}
        </KText>
      </Flex>
    );
  };

  return (
    <KDrawer
      isOpen={isOpen}
      onClose={onClose}
      header={renderHeader()}
      skipFooter={true}
      contentProps={{
        maxW: '700px',
        borderTopLeftRadius: 'xl',
      }}
    >
      <Flex flexDir="column" minH="100%">
        {isGetLoading || isGetSurveyLoading ? (
          <CenterSpinner flex="1" />
        ) : (
          <Flex flexDir="column" minH="100%">
            {renderContent()}
          </Flex>
        )}
      </Flex>
    </KDrawer>
  );
};
